import React from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  useTheme, 
  useMediaQuery,
  Skeleton
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  Speed, 
  CheckCircle, 
  Error, 
  Schedule 
} from '@mui/icons-material';

const MetricsCard = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  trend, 
  trendValue, 
  color = 'primary',
  loading = false,
  size = 'medium'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getIconComponent = (iconName) => {
    const iconMap = {
      trendingUp: TrendingUp,
      trendingDown: TrendingDown,
      speed: Speed,
      checkCircle: CheckCircle,
      error: Error,
      schedule: Schedule
    };
    return iconMap[iconName] || TrendingUp;
  };

  const getColor = (colorName) => {
    const colorMap = {
      primary: theme.palette.primary.main,
      success: theme.palette.success.main,
      error: theme.palette.error.main,
      warning: theme.palette.warning.main,
      info: theme.palette.info.main
    };
    return colorMap[colorName] || theme.palette.primary.main;
  };

  const IconComponent = getIconComponent(icon);

  if (loading) {
    return (
      <Card sx={{ 
        height: size === 'small' ? 120 : 140,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: theme.shadows[2],
        borderRadius: '6px',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}>
        <CardContent sx={{ flexGrow: 1, p: isMobile ? 2 : 3 }}>
          <Skeleton variant="text" width="60%" height={24} />
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="text" width="80%" height={20} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      sx={{
        height: size === 'small' ? 120 : 140,
        display: "flex",
        flexDirection: "column",
        boxShadow: theme.shadows[2],
        borderRadius: '6px',
        '&:hover': {
          boxShadow: theme.shadows[4],
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      <CardContent sx={{ 
        flexGrow: 1, 
        p: isMobile ? 2 : 2.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography 
            variant={isMobile ? "body2" : "body1"} 
            color="text.secondary" 
            sx={{ fontWeight: 500 }}
          >
            {title}
          </Typography>
          {icon && (
            <Box sx={{ 
              p: 1, 
              borderRadius: 1, 
              backgroundColor: `${getColor(color)}15`,
              color: getColor(color)
            }}>
              {icon}
            </Box>
          )}
        </Box>
        
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="div" 
            sx={{ 
              fontWeight: 700,
              color: getColor(color),
              mb: 0.5
            }}
          >
            {value}
          </Typography>
          
          {subtitle && (
            <Typography 
              variant={isMobile ? "caption" : "body2"} 
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {subtitle}
            </Typography>
          )}
          
          {trend && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5 
            }}>
              {trend === 'up' ? (
                <TrendingUp sx={{ 
                  fontSize: 16, 
                  color: theme.palette.success.main 
                }} />
              ) : (
                <TrendingDown sx={{ 
                  fontSize: 16, 
                  color: theme.palette.error.main 
                }} />
              )}
              <Typography 
                variant="caption" 
                sx={{ 
                  color: trend === 'up' ? 'success.main' : 'error.main',
                  fontWeight: 500
                }}
              >
                {trendValue}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default MetricsCard;
